import {
  Center,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Spinner,
  useDisclosure,
} from "@chakra-ui/react";
import React, { memo, useCallback } from "react";
import dynamic from "next/dynamic";
import colors from "app/colors";
import { EmojiIcon } from "modules/icons";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import ClickOutsideBackdrop from "modules/_shared/click-outside-backdrop";

interface IProps {
  draftId: string;
  appendText: (value: string) => void;
}

// The emoji picker and its data set are heavy and rarely used, so they are
// code-split into their own chunk (kept out of the compose/thread bundle) and
// only fetched on demand. `next/dynamic` with `ssr: false` is a pure
// client-side split — fully compatible with `output: "export"`.
const LazyEmojiPicker = dynamic(() => import("./emoji-picker-lazy"), {
  ssr: false,
  loading: () => (
    <Center w="340px" h="400px" bg="white" rounded="lg" boxShadow="lg">
      <Spinner />
    </Center>
  ),
});

// Snappier open/close than Chakra's default popover transition (enter 0.15s /
// exit 0.1s) so the picker feels responsive. Overriding `variants` is required
// because the per-variant `transition` takes precedence over a top-level one.
const pickerMotionProps = {
  variants: {
    enter: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.09, ease: "easeOut" },
    },
    exit: {
      scale: 0.95,
      opacity: 0,
      transition: { duration: 0.07, ease: "easeIn" },
    },
  },
} as const;

// Kick off loading the chunk a beat before it is needed — on hover/focus of the
// button — so opening feels instant when the picker is actually used, without
// paying that cost for the many threads where it never is.
const prefetchEmojiPicker = () => {
  import("./emoji-picker-lazy").catch(() => {
    /* best-effort prefetch; the picker still loads on open */
  });
};

const ComposeBottomToolbarEmojiButton = ({ draftId, appendText }: IProps) => {
  const { draft, isDraftLoaded, bottomButtonTypes } = useMessageThreadDraft({
    draftId,
  });

  const { onOpen, onClose, isOpen } = useDisclosure();

  const onEmojiSelect = useCallback(
    (native: string) => {
      appendText(native);
      onClose();
    },
    [appendText, onClose],
  );

  if (!draft || !isDraftLoaded) return null;

  if (!bottomButtonTypes.includes("emoji")) {
    return null;
  }

  return (
    <Popover
      placement="top"
      onOpen={onOpen}
      isOpen={isOpen}
      isLazy
      autoFocus={false}
      returnFocusOnClose={false}
    >
      <PopoverTrigger>
        <CircularIconButton
          aria-label="Add emoji"
          title="Add emoji"
          size="xs"
          variant="ghost"
          color={colors.gray40}
          icon={<EmojiIcon fill={colors.gray40} boxSize={5} />}
          onClick={onOpen}
          onMouseEnter={prefetchEmojiPicker}
          onFocus={prefetchEmojiPicker}
          tabIndex={-1}
        />
      </PopoverTrigger>
      <Portal>
        <ClickOutsideBackdrop isOpen={isOpen} onClose={onClose} />
        <PopoverContent
          w="auto"
          bg="transparent"
          border="none"
          boxShadow="none"
          motionProps={pickerMotionProps}
          _focusVisible={{ outline: "none" }}
        >
          <LazyEmojiPicker onSelect={onEmojiSelect} />
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default memo(ComposeBottomToolbarEmojiButton);
